import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { getMyEmployee } from "../../api/employees";
import {
  approveWfhRequest,
  cancelWfhRequest,
  createWfhRequest,
  getWfhRequests,
  rejectWfhRequest,
} from "../../api/wfh";
import {
  canRequestWfh,
  normalizeWorkMode,
} from "../../utils/workModeHelpers";
import {
  canUserApproveWfh,
  resolveUserSlugs,
} from "../../utils/wfhHelpers";
import { formatWorkplaceLabel } from "../../utils/dataMappers";
import { useNavigation } from "../../context/NavigationContext";
import { buildResubmitDraft } from "../../utils/requestTransparency";
import RequestTransparency from "../common/RequestTransparency";
import "./WfhRequestForm.css";

const STATUS_LABELS = {
  PENDING: "قيد المراجعة",
  APPROVED: "معتمد",
  REJECTED: "مرفوض",
  CANCELLED: "ملغى",
  IN_PROGRESS: "قيد المراجعة",
};

const TYPE_LABELS = {
  DAILY: "يومي",
  WEEKLY: "أسبوعي",
};

const CYCLE_STEPS = [
  { slug: "manager", label: "المدير المباشر" },
  { slug: "department_manager", label: "مدير الإدارة" },
  { slug: "hr", label: "الموارد البشرية" },
];

function formatDate(value) {
  if (!value) return "—";
  return String(value).slice(0, 10);
}

function WfhRequestForm() {
  const { user } = useAuth();
  const { employeesByBranch, updateWfhRequests } = useData();
  const navigation = useNavigation();
  const profile = useMemo(() => {
    const all = Object.values(employeesByBranch || {}).flat();
    return all.find(
      (e) => e.id === user?.employeeId || e.userId === user?.id,
    );
  }, [employeesByBranch, user?.employeeId, user?.id]);
  const [workMode, setWorkMode] = useState(
    normalizeWorkMode(profile?.workMode || user?.workMode),
  );
  const canSubmit = canRequestWfh(workMode);
  const userSlugs = resolveUserSlugs(user);
  const canApprove =
    userSlugs.includes("manager") ||
    userSlugs.includes("department_manager") ||
    userSlugs.includes("hr") ||
    userSlugs.includes("hr_staff") ||
    userSlugs.includes("gm") ||
    userSlugs.includes("admin") ||
    user?.role === "manager" ||
    user?.role === "department-manager" ||
    user?.role === "hr" ||
    user?.role === "hr-staff" ||
    user?.role === "gm";

  const [type, setType] = useState("DAILY");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [reason, setReason] = useState("");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tab, setTab] = useState(canApprove ? "team" : "mine");

  useEffect(() => {
    if (profile?.workMode) setWorkMode(normalizeWorkMode(profile.workMode));
  }, [profile?.workMode]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [data, me] = await Promise.all([
        getWfhRequests(),
        getMyEmployee().catch(() => null),
      ]);
      if (me?.workMode) setWorkMode(normalizeWorkMode(me.workMode));
      const rows = Array.isArray(data) ? data : data?.items || [];
      setRequests(rows);
      updateWfhRequests(
        rows.filter((r) => String(r.status || "").toUpperCase() === "PENDING"),
      );
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "تعذر تحميل طلبات العمل من المنزل";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setLoading(false);
    }
  }, [updateWfhRequests]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const draft = navigation?.requestDraft;
    if (!draft || draft.kind !== "wfh") return undefined;
    setType(draft.wfhType || "DAILY");
    setDateFrom(draft.dateFrom || "");
    setDateTo(draft.dateTo || "");
    setReason(draft.reason || "");
    navigation.clearRequestDraft?.();
    return undefined;
  }, [navigation?.requestDraft]);

  const myRequests = useMemo(
    () =>
      requests.filter(
        (r) =>
          r.employeeId === user?.employeeId ||
          r.employee?.id === user?.employeeId,
      ),
    [requests, user?.employeeId],
  );

  const teamPending = useMemo(
    () =>
      requests.filter(
        (r) =>
          r.status === "PENDING" &&
          r.employeeId !== user?.employeeId &&
          r.employee?.id !== user?.employeeId &&
          canUserApproveWfh(r, user),
      ),
    [requests, user],
  );

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!canSubmit) {
      setError("طلبات العمل عن بُعد متاحة فقط للموظفين بنظام مختلط");
      return;
    }
    if (!dateFrom) {
      setError("من فضلك اختر التاريخ");
      return;
    }
    if (type === "WEEKLY" && !dateTo) {
      setError("من فضلك اختر تاريخ النهاية للأسبوع");
      return;
    }

    setSubmitting(true);
    try {
      await createWfhRequest({
        type,
        dateFrom,
        ...(type === "WEEKLY" ? { dateTo } : {}),
        reason: reason.trim() || undefined,
      });
      setSuccess("تم تقديم الطلب — بانتظار موافقة المدير المباشر ثم مدير الإدارة ثم HR");
      setDateFrom("");
      setDateTo("");
      setReason("");
      await refresh();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "تعذر تقديم الطلب";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDecision(id, status) {
    setActingId(id);
    setError("");
    setSuccess("");
    try {
      if (status === "APPROVED") {
        await approveWfhRequest(id);
        setSuccess("تمت الموافقة على المرحلة");
      } else {
        await rejectWfhRequest(id);
        setSuccess("تم رفض الطلب");
      }
      await refresh();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "تعذر تحديث حالة الطلب";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setActingId(null);
    }
  }

  async function handleCancel(id) {
    if (
      !window.confirm(
        "هل تريد إلغاء طلب العمل من المنزل؟ يمكنك تقديم طلب جديد بعد الإلغاء.",
      )
    ) {
      return;
    }
    setActingId(id);
    setError("");
    setSuccess("");
    try {
      await cancelWfhRequest(id);
      setSuccess("تم إلغاء الطلب بنجاح");
      await refresh();
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "تعذر إلغاء الطلب";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setActingId(null);
    }
  }

  const list =
    tab === "team" && canApprove ? teamPending : myRequests;

  return (
    <div className="wfh-page" dir="rtl">
      <div className="ess-home-header">
        <h1 className="ess-home-title">العمل عن بُعد</h1>
        <p className="ess-home-subtitle">
          سايكل الموافقة: الموظف → المدير المباشر → مدير الإدارة → الموارد البشرية
        </p>
      </div>

      <div className="wfh-cycle">
        {CYCLE_STEPS.map((step, index) => (
          <React.Fragment key={step.slug}>
            {index > 0 && <span className="wfh-cycle-arrow">←</span>}
            <span className="wfh-cycle-step">{step.label}</span>
          </React.Fragment>
        ))}
      </div>

      {workMode !== "HYBRID" && (
        <div className="wfh-banner">
          {workMode === "REMOTE" ? (
            <>
              نظامك <strong>عمل عن بُعد دائم</strong> — لا تحتاج طلب أيام عمل عن
              بُعد. سجّل الحضور والانصراف يوميًا من صفحة «حضور وانصراف التطبيق».
            </>
          ) : (
            <>
              تقديم الطلبات متاح فقط إذا كان نظام عملك{" "}
              <strong>مختلط</strong>. نظامك الحالي: مكتب (بصمة).
            </>
          )}
        </div>
      )}

      {canSubmit && (
        <form className="wfh-card" onSubmit={handleSubmit}>
          <h3 className="wfh-card-title">طلب جديد</h3>

          <div className="wfh-field">
            <label>نوع الطلب</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="DAILY">يومي</option>
              <option value="WEEKLY">أسبوعي</option>
            </select>
          </div>

          <div className="wfh-row">
            <div className="wfh-field">
              <label>{type === "WEEKLY" ? "من تاريخ" : "التاريخ"}</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                required
              />
            </div>
            {type === "WEEKLY" && (
              <div className="wfh-field">
                <label>إلى تاريخ</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  required
                />
              </div>
            )}
          </div>

          <div className="wfh-field">
            <label>السبب (اختياري)</label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="مثال: اجتماع عملاء عن بُعد"
              maxLength={500}
            />
          </div>

          <button
            type="submit"
            className="wfh-btn wfh-btn-primary"
            disabled={submitting}
          >
            {submitting ? "جاري الإرسال..." : "تقديم الطلب"}
          </button>
        </form>
      )}

      <div className="wfh-card">
        <div className="wfh-tabs">
          <button
            type="button"
            className={`wfh-tab${tab === "mine" ? " active" : ""}`}
            onClick={() => setTab("mine")}
          >
            طلباتي
          </button>
          {canApprove && (
            <button
              type="button"
              className={`wfh-tab${tab === "team" ? " active" : ""}`}
              onClick={() => setTab("team")}
            >
              بانتظار موافقتي ({teamPending.length})
            </button>
          )}
        </div>

        {loading ? (
          <p className="wfh-muted">جاري التحميل...</p>
        ) : list.length === 0 ? (
          <p className="wfh-muted">لا توجد طلبات</p>
        ) : (
          <div className="wfh-list">
            {list.map((row) => {
              const showActions =
                tab === "team" && canUserApproveWfh(row, user);
              const canCancelOwn =
                tab === "mine" &&
                row.status === "PENDING" &&
                (row.employeeId === user?.employeeId ||
                  row.employee?.id === user?.employeeId ||
                  row.request?.createdById === user?.id);
              const stepLabel =
                row.currentStepLabel ||
                row.nextApprover?.roleName ||
                null;

              return (
                <div key={row.id} className="wfh-item">
                  <div className="wfh-item-head">
                    <strong>
                      {row.employee?.fullName || "طلب عمل من المنزل"}
                    </strong>
                    <span
                      className={`wfh-status wfh-status-${String(
                        row.status || "",
                      ).toLowerCase()}`}
                    >
                      {STATUS_LABELS[row.status] || row.status}
                    </span>
                  </div>
                  {formatWorkplaceLabel(
                    row.employee?.branch?.name,
                    row.employee?.department?.name,
                  ) ? (
                    <div className="wfh-item-workplace">
                      {formatWorkplaceLabel(
                        row.employee?.branch?.name,
                        row.employee?.department?.name,
                      )}
                    </div>
                  ) : null}
                  <div className="wfh-item-meta">
                    <span>{TYPE_LABELS[row.type] || row.type}</span>
                    <span>
                      {formatDate(row.dateFrom)}
                      {row.dateTo && row.dateTo !== row.dateFrom
                        ? ` → ${formatDate(row.dateTo)}`
                        : ""}
                    </span>
                  </div>
                  {row.status === "PENDING" && stepLabel && (
                    <p className="wfh-item-step">
                      المرحلة الحالية: {stepLabel}
                      {row.currentStep ? ` (${row.currentStep}/3)` : ""}
                    </p>
                  )}
                  {row.reason && <p className="wfh-item-reason">{row.reason}</p>}
                  {tab === "mine" ? (
                    <RequestTransparency
                      request={{
                        ...row,
                        _source: "wfh",
                        type: "عمل عن بُعد",
                        status:
                          String(row.status || "").toLowerCase() === "rejected"
                            ? "rejected"
                            : String(row.status || "").toLowerCase() === "approved"
                              ? "approved"
                              : "pending",
                      }}
                      compact
                      onResubmit={(item) => {
                        const draft = buildResubmitDraft({
                          ...item,
                          _source: "wfh",
                          type: "عمل عن بُعد",
                          dateFrom: row.dateFrom,
                          dateTo: row.dateTo,
                          wfhType: row.type,
                          reason: row.reason,
                        });
                        if (!draft) return;
                        setType(draft.wfhType || "DAILY");
                        setDateFrom(draft.dateFrom || "");
                        setDateTo(draft.dateTo || "");
                        setReason(draft.reason || "");
                      }}
                    />
                  ) : null}
                  {showActions && (
                    <div className="wfh-item-actions">
                      <button
                        type="button"
                        className="wfh-btn wfh-btn-approve"
                        disabled={actingId === row.id}
                        onClick={() => handleDecision(row.id, "APPROVED")}
                      >
                        موافقة
                      </button>
                      <button
                        type="button"
                        className="wfh-btn wfh-btn-reject"
                        disabled={actingId === row.id}
                        onClick={() => handleDecision(row.id, "REJECTED")}
                      >
                        رفض
                      </button>
                    </div>
                  )}
                  {canCancelOwn && (
                    <div className="wfh-item-actions">
                      <button
                        type="button"
                        className="wfh-btn wfh-btn-cancel"
                        disabled={actingId === row.id}
                        onClick={() => handleCancel(row.id)}
                      >
                        {actingId === row.id ? "جاري الإلغاء..." : "إلغاء الطلب"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {error && <div className="wfh-error">{error}</div>}
      {success && <div className="wfh-success">{success}</div>}
    </div>
  );
}

export default WfhRequestForm;
